import { useQueries } from '@tanstack/react-query';

import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import {
  getTablesAvailability,
  getTablesAvailabilityKey,
} from '@features/tables/hooks/useGetTablesAvailability';
import { MappedRestaurantsType } from '@features/tables/schemas/MappedRestaurantResponse.schema';
import {
  PromotionsType,
  TablesAvailabilityType,
  TimeSlotsType,
} from '@features/tables/schemas/TablesAvailability.schema';

import { usePackageConfiguration } from '../../contexts';
import { DiningDateGroup } from '../../helpers/groupDiningOptionsByDate';
import { filterTimeslotsByMenuOrTimeRestriction } from '../TablesStep/helpers/filterTimeslotsByMenuOrTimeRestrictions';

export type PackageDiningOptionAvailability = {
  restaurant?: MappedRestaurantsType[number];
  promotions: PromotionsType;
  timeSlots: TimeSlotsType;
  isFetching: boolean;
};

export function usePackageDiningAvailability(
  dateGroups: DiningDateGroup[]
): Record<number, PackageDiningOptionAvailability> {
  const { property, language, journeyParams } = usePackageConfiguration();
  const { data: restaurants } = useGetRestaurants(property.id, language);

  const entries = dateGroups.flatMap(({ date, options }) =>
    options.map(({ option, optionIndex }) => ({
      date,
      option,
      optionIndex,
      restaurant: restaurants?.find(({ config }) => config.id === option.id),
    }))
  );

  const queries = useQueries({
    queries: entries.map(({ date, restaurant }) => {
      const params = {
        propertyId: property.id,
        date,
        restaurant: restaurant?.restaurantName || '',
        restaurantId: restaurant?.config.id,
        adults: journeyParams.adults,
        children: journeyParams.children ?? 0,
      };

      return {
        queryKey: getTablesAvailabilityKey(params),
        queryFn: () => getTablesAvailability(params),
        staleTime: FIVE_MINUTES,
        cacheTime: FIVE_MINUTES,
        enabled: !!restaurant,
      };
    }),
  });

  return Object.fromEntries(
    entries.map(({ optionIndex, option, restaurant }, index) => {
      const query = queries[index];
      const data = query?.data as TablesAvailabilityType | undefined;

      return [
        optionIndex,
        {
          restaurant,
          promotions: data?.promotions || [],
          timeSlots: filterTimeslotsByMenuOrTimeRestriction(data?.timeSlots || [], option),
          isFetching: !!restaurant && !!query?.isFetching,
        },
      ];
    })
  );
}
